/* ============================================================================
   Rolex search routing
   ----------------------------------------------------------------------------
   ONE rule, two layers:

     match a Rolex keyword  ->  /pages/rolex-search
     anything else          ->  q must carry
                                ` -vendor:Rolex -vendor:"Rolex Certified Pre-Owned"`

   Layer 1 intercepts every search FORM submit (header, search page, blog
   sidebar, 404 — whatever the selector finds), so the shopper never loads an
   unfiltered results page.

   Layer 2 guards the results page itself and catches what Layer 1 cannot:
   direct /search?q= links, bookmarks, pasted URLs, external referrers.
   It runs SYNCHRONOUSLY at parse time (this file is loaded without `defer`
   from header-new, which renders before content_for_layout) so the redirect
   happens before any un-excluded product markup is parsed.

   PROVENANCE — read this before changing the list.
   The original implementation could not be recovered: it is in neither this
   theme nor the old theme export (theme_export__...24AUG2026), whose header
   search is a plain GET with no interception and whose search-form.js is stock
   Dawn. There is no `getCustomSetting` in either codebase. So the keyword list
   is NOT hardcoded here — it is read from the `rolex_search_keywords` theme
   setting (Theme settings -> Rolex search), which ships EMPTY. While it is
   empty, no query is ever diverted to the Rolex page; the vendor exclusions
   below still apply everywhere, because those two strings were specified
   exactly. Paste the client's list into that setting to switch keyword routing
   on — no code change needed.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.MANFREDI_ROLEX_SEARCH || {};
  var ROLEX_PAGE = CFG.page || '/pages/rolex-search';

  /* EXACT string, leading space included. Do not reformat: the quoting and the
     spacing are what Shopify's search parser matches on. */
  var EXCLUSIONS = ' -vendor:Rolex -vendor:"Rolex Certified Pre-Owned"';

  /* Kept verbatim from the client's snippet: greedy, so it strips the first
     ` -vendor:Rolex` and EVERYTHING after it to the end of the string. That is
     deliberate — it is what makes re-submitting an already-decorated q
     idempotent rather than stacking a second copy of the exclusions. */
  var STRIP_RE = / -vendor:Rolex.*/i;
  var HAS_RE = / -vendor:Rolex/i;

  function str(v) {
    return v == null ? '' : String(v);
  }

  function keywords() {
    var cfg = window.MANFREDI_ROLEX_SEARCH;
    return (cfg && cfg.keywords) || [];
  }

  function stripExclusions(q) {
    return str(q).replace(STRIP_RE, '');
  }

  function hasExclusions(q) {
    return HAS_RE.test(str(q));
  }

  function withExclusions(cleanQ) {
    return cleanQ + EXCLUSIONS;
  }

  /* Case-insensitive SUBSTRING match against the whole cleaned query, so
     "rolex daytona" and "daytonas" both match the term "daytona".
     Semantics confirmed with the client; see PROVENANCE above. */
  function matchesRolex(q) {
    var hay = str(q).toLowerCase();
    if (!hay) return false;
    var list = keywords();
    for (var i = 0; i < list.length; i++) {
      var term = str(list[i]).toLowerCase().trim();
      if (term && hay.indexOf(term) !== -1) return true;
    }
    return false;
  }

  /* Preserves the form's other fields (type=product, options[prefix]=last …)
     so intercepting the submit does not quietly drop them. */
  function buildParams(form) {
    var params = new URLSearchParams();
    var els = form.elements;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.name || el.disabled) continue;
      if (el.type === 'submit' || el.type === 'button' || el.type === 'file') continue;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;
      params.append(el.name, el.value);
    }
    return params;
  }

  /* ---- Layer 1: form submit ---------------------------------------------- */
  function onSearchSubmit(event) {
    var form = event.currentTarget;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    // Strip first, so a q that already carries the exclusions (the results
    // page's own form re-submitting) is matched and re-decorated cleanly
    // instead of accumulating a second copy.
    var clean = stripExclusions(input.value).trim();

    event.preventDefault();
    if (!clean) return; // empty query does nothing, as before

    if (matchesRolex(clean)) {
      window.location.assign(ROLEX_PAGE);
      return;
    }

    // The URL is built rather than the visible input mutated, so the shopper
    // never sees ` -vendor:Rolex …` flash into the search box.
    var action = form.getAttribute('action') || '/search';
    var params = buildParams(form);
    params.set('q', withExclusions(clean));
    window.location.assign(action + '?' + params.toString());
  }

  function bindForms(scope) {
    var root = scope || document;
    var forms = root.querySelectorAll('form[action*="/search"]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.hasAttribute('data-rolex-search-bound')) return;
      if (form.hasAttribute('data-rolex-search-skip')) return;
      if (!form.querySelector('input[name="q"]')) return;
      form.setAttribute('data-rolex-search-bound', '');
      form.addEventListener('submit', onSearchSubmit);
    });
  }

  /* ---- Layer 2: results-page safety net ----------------------------------
     replace(), not assign(), so the un-routed URL never lands in history and
     Back does not bounce the shopper through the redirect. */
  function guardResultsPage() {
    if (window.location.pathname.indexOf('/search') === -1) return;

    var params = new URLSearchParams(window.location.search);
    var raw = params.get('q');
    if (raw === null) return;

    var clean = stripExclusions(raw).trim();
    if (!clean) return;

    if (matchesRolex(clean)) {
      window.location.replace(ROLEX_PAGE);
      return;
    }

    // Already decorated — leave it alone. This is what stops the redirect loop.
    if (hasExclusions(raw)) return;

    params.set('q', withExclusions(clean));
    window.location.replace(window.location.pathname + '?' + params.toString());
  }

  // Layer 2 runs NOW, at parse time, ahead of the results markup.
  guardResultsPage();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindForms(document); });
  } else {
    bindForms(document);
  }

  window.ManfrediRolexSearch = {
    EXCLUSIONS: EXCLUSIONS,
    matchesRolex: matchesRolex,
    stripExclusions: stripExclusions,
    hasExclusions: hasExclusions,
    withExclusions: withExclusions,
    bindForms: bindForms,
    guardResultsPage: guardResultsPage
  };
})();
